import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessageSquare, DollarSign, Users, Globe, Zap, Clock,
  AlertTriangle, Target, TrendingUp, TrendingDown, GitBranch, Search,
  ChevronDown, ChevronUp, X, Download, RefreshCw, ArrowLeft,
  BarChart2, FileText, Layers, Brain
} from 'lucide-react';
import { apiCall } from './Api';
import { useAuth } from './contexts/AuthContext';
import IntelligenceLibrary from './IntelligenceLibrary';

// ─── Icon resolver ────────────────────────────────────────────
const ICON_MAP = {
  MessageSquare, DollarSign, Users, Globe, Zap, Clock,
  AlertTriangle, Target, TrendingUp, TrendingDown, GitBranch, Search,
  BarChart2, FileText, Layers
};
const resolveIcon = (name) => ICON_MAP[name] || FileText;

// ─── Formatters (matching HierarchicalTable) ──────────────────
const formatNumber = (num) => {
  if (num == null) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toLocaleString() || '0';
};
const formatCurrency = (amount) => formatNumber(amount);
const formatPercentage = (value) => `${(value || 0).toFixed(2)}%`;
const formatDuration = (ms) => {
  if (!ms) return '0ms';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
};
const getStatusBadge = (rate) => {
  if (rate >= 95) return 'bg-green-100 text-green-800';
  if (rate >= 90) return 'bg-yellow-100 text-yellow-800';
  if (rate >= 80) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

// Column keys that must not appear for customer-scoped users. Mirrors the
// SENSITIVE_FIELDS set in the backend's customer_redaction module — any new
// vendor/cost/profit/fake column added to REPORT_COLUMNS should be added here.
const CUSTOMER_HIDDEN_KEYS = new Set([
  'vendor_name', 'vendor_id', 'vendor_account_manager',
  'cost', 'total_cost', 'cost_per_msg', 'cost_per_message',
  'profit', 'total_profit', 'profit_margin', 'margin_pct', 'margin_percentage',
  'fake_dlr_count', 'fake_pct', 'fake_percentage',
  'affected_vendors', 'vendors',
]);

// ─── Category config ──────────────────────────────────────────
const CATEGORIES = [
  { key: 'all',        label: 'All Reports',   icon: Layers },
  { key: 'traffic',    label: 'Traffic',        icon: MessageSquare },
  { key: 'financial',  label: 'Financial',      icon: DollarSign },
  { key: 'quality',    label: 'Quality',        icon: Zap },
  { key: 'geographic', label: 'Geographic',     icon: Globe },
  { key: 'routing',    label: 'Routing',        icon: GitBranch },
];

// ═══════════════════════════════════════════════════════════════
// Per-report column schemas
// ═══════════════════════════════════════════════════════════════

const REPORT_COLUMNS = {
  customer_ranking: [
    { key: 'customer_name', label: 'Customer', sticky: true, searchable: true },
    { key: 'account_manager', label: 'Account Manager', searchable: true },
    { key: 'total_messages', label: 'Total Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600', sortable: true },
    { key: 'failed', label: 'Failed', format: 'number', color: 'text-red-600', sortable: true },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge', sortable: true },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'profit', label: 'Profit', format: 'profit', sortable: true },
    { key: 'unique_destinations', label: 'Destinations', format: 'number', color: 'text-orange-600', sortable: true },
    { key: 'countries', label: 'Countries', format: 'number', color: 'text-blue-600' },
  ],
  vendor_ranking: [
    { key: 'vendor_name', label: 'Vendor', sticky: true, searchable: true },
    { key: 'account_manager', label: 'Account Manager', searchable: true },
    { key: 'total_messages', label: 'Total Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600', sortable: true },
    { key: 'failed', label: 'Failed', format: 'number', color: 'text-red-600', sortable: true },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge', sortable: true },
    { key: 'cost', label: 'Cost', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'profit', label: 'Profit', format: 'profit', sortable: true },
    { key: 'avg_delivery_time', label: 'Avg Delivery', format: 'duration', color: 'text-gray-900', sortable: true },
    { key: 'countries', label: 'Countries', format: 'number', color: 'text-blue-600' },
  ],
  revenue_profitability: [
    { key: 'customer_name', label: 'Customer', sticky: true, searchable: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'total_revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'total_cost', label: 'Cost', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'total_profit', label: 'Profit', format: 'profit', sortable: true },
    { key: 'margin_pct', label: 'Margin %', format: 'margin_pct', sortable: true },
    { key: 'revenue_per_msg', label: 'Rev/Msg', format: 'currency_precise', color: 'text-gray-600' },
    { key: 'cost_per_msg', label: 'Cost/Msg', format: 'currency_precise', color: 'text-gray-600' },
  ],
  destination_analysis: [
    { key: 'country_name', label: 'Country', sticky: true, searchable: true },
    { key: 'country_code', label: 'Code' },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600', sortable: true },
    { key: 'failed', label: 'Failed', format: 'number', color: 'text-red-600', sortable: true },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge', sortable: true },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'cost', label: 'Cost', format: 'currency', color: 'text-gray-900' },
    { key: 'profit', label: 'Profit', format: 'profit', sortable: true },
    { key: 'avg_delivery_time', label: 'Avg Delivery', format: 'duration', color: 'text-gray-900', sortable: true },
    { key: 'customers', label: 'Customers', format: 'number', color: 'text-blue-600' },
    { key: 'vendors', label: 'Vendors', format: 'number', color: 'text-purple-600' },
  ],
  network_performance: [
    { key: 'network_name', label: 'Network', sticky: true, searchable: true },
    { key: 'mcc', label: 'MCC' },
    { key: 'mnc', label: 'MNC' },
    { key: 'country_name', label: 'Country', searchable: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600' },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge', sortable: true },
    { key: 'error_rate', label: 'Error Rate', format: 'error_pct', sortable: true },
    { key: 'avg_delivery_time', label: 'Avg Delivery', format: 'duration', color: 'text-gray-900', sortable: true },
    { key: 'avg_submit_time', label: 'Avg Submit', format: 'duration', color: 'text-gray-900' },
    { key: 'fake_dlr_count', label: 'Fake DLR', format: 'number', color: 'text-red-600' },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900' },
  ],
  delivery_latency: [
    { key: 'vendor_name', label: 'Vendor', sticky: true, searchable: true },
    { key: 'country_name', label: 'Country', searchable: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900' },
    { key: 'avg_submit_ms', label: 'Avg Submit', format: 'duration', color: 'text-gray-900' },
    { key: 'avg_forward_ms', label: 'Avg Forward', format: 'duration', color: 'text-gray-900' },
    { key: 'avg_delivery_ms', label: 'Avg Delivery', format: 'duration', color: 'text-gray-900' },
    { key: 'p50_delivery_ms', label: 'P50', format: 'duration', color: 'text-blue-600' },
    { key: 'p95_delivery_ms', label: 'P95', format: 'duration', color: 'text-orange-600' },
    { key: 'p99_delivery_ms', label: 'P99', format: 'duration', color: 'text-red-600' },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge' },
  ],
  error_analysis: [
    { key: 'error_code', label: 'Error Code', sticky: true },
    { key: 'error_count', label: 'Count', format: 'number', color: 'text-red-600', sortable: true },
    { key: 'affected_customers', label: 'Customers', format: 'number', color: 'text-blue-600' },
    { key: 'affected_vendors', label: 'Vendors', format: 'number', color: 'text-purple-600' },
    { key: 'lost_revenue', label: 'Lost Revenue', format: 'currency', color: 'text-red-600' },
    { key: 'avg_retries', label: 'Avg Retries', format: 'decimal', color: 'text-yellow-600' },
    { key: 'countries', label: 'Countries', format: 'array' },
  ],
  sender_id_report: [
    { key: 'sender_id', label: 'Sender ID', sticky: true, searchable: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600' },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge', sortable: true },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'unique_destinations', label: 'Destinations', format: 'number', color: 'text-orange-600', sortable: true },
    { key: 'customers', label: 'Customers', format: 'number', color: 'text-blue-600' },
    { key: 'countries', label: 'Countries', format: 'number', color: 'text-purple-600' },
  ],
  fake_dlr_report: [
    { key: 'vendor_name', label: 'Vendor', sticky: true, searchable: true },
    { key: 'country_name', label: 'Country', searchable: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900' },
    { key: 'fake_count', label: 'Fake Count', format: 'number', color: 'text-red-600' },
    { key: 'fake_pct', label: 'Fake %', format: 'error_pct' },
    { key: 'cost', label: 'Cost', format: 'currency', color: 'text-gray-900' },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600' },
  ],
  daily_trend: [
    { key: 'date', label: 'Date', sticky: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900' },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600' },
    { key: 'failed', label: 'Failed', format: 'number', color: 'text-red-600' },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge' },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900' },
    { key: 'cost', label: 'Cost', format: 'currency', color: 'text-gray-900' },
    { key: 'profit', label: 'Profit', format: 'profit' },
  ],
  hourly_pattern: [
    { key: 'hour', label: 'Hour', sticky: true, render: (v) => `${v}:00` },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900' },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600' },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge' },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900' },
  ],
  margin_leakage: [
    { key: 'customer_name', label: 'Customer', sticky: true, searchable: true },
    { key: 'vendor_name', label: 'Vendor', searchable: true },
    { key: 'country_name', label: 'Country', searchable: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900' },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900' },
    { key: 'cost', label: 'Cost', format: 'currency', color: 'text-gray-900' },
    { key: 'profit', label: 'Profit', format: 'profit' },
    { key: 'margin_pct', label: 'Margin %', format: 'margin_pct' },
  ],
  customer_vendor_matrix: [
    { key: 'customer_name', label: 'Customer', sticky: true, searchable: true },
    { key: 'vendor_name', label: 'Vendor', searchable: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge', sortable: true },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'cost', label: 'Cost', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'profit', label: 'Profit', format: 'profit', sortable: true },
    { key: 'margin_pct', label: 'Margin %', format: 'margin_pct', sortable: true },
    { key: 'countries', label: 'Countries', format: 'number', color: 'text-blue-600' },
  ],
  campaign_performance: [
    { key: 'campaign_id', label: 'Campaign', sticky: true, searchable: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600' },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge', sortable: true },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'profit', label: 'Profit', format: 'profit', sortable: true },
    { key: 'countries', label: 'Countries', format: 'number', color: 'text-blue-600' },
    { key: 'start_date', label: 'Start' },
    { key: 'end_date', label: 'End' },
  ],
  lookup_hlr_report: [
    { key: 'lookup_vendor_name', label: 'HLR Vendor', sticky: true },
    { key: 'lookup_count', label: 'Lookups', format: 'number', color: 'text-gray-900' },
    { key: 'lookup_cost', label: 'Lookup Cost', format: 'currency', color: 'text-red-600' },
    { key: 'total_messages', label: 'Total Messages', format: 'number', color: 'text-gray-900' },
    { key: 'lookup_pct', label: 'Lookup %', format: 'percentage', color: 'text-gray-900' },
  ],
  account_manager_report: [
    { key: 'manager_type', label: 'Type', sticky: true },
    { key: 'manager_name', label: 'Manager', searchable: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600', sortable: true },
    { key: 'failed', label: 'Failed', format: 'number', color: 'text-red-600' },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge', sortable: true },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'cost', label: 'Cost', format: 'currency', color: 'text-gray-900' },
    { key: 'profit', label: 'Profit', format: 'profit', sortable: true },
    { key: 'customers', label: 'Customers', format: 'number', color: 'text-blue-600', sortable: true },
    { key: 'vendors', label: 'Vendors', format: 'number', color: 'text-purple-600', sortable: true },
    { key: 'countries', label: 'Countries', format: 'number', color: 'text-orange-600' },
  ],
  monthly_trend: [
    { key: 'month', label: 'Month', sticky: true, render: (v) => { const s = String(v); return s.length === 6 ? `${s.slice(0,4)}-${s.slice(4)}` : s; } },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900' },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600' },
    { key: 'failed', label: 'Failed', format: 'number', color: 'text-red-600' },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge' },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900' },
    { key: 'cost', label: 'Cost', format: 'currency', color: 'text-gray-900' },
    { key: 'profit', label: 'Profit', format: 'profit' },
    { key: 'customers', label: 'Customers', format: 'number', color: 'text-blue-600' },
    { key: 'vendors', label: 'Vendors', format: 'number', color: 'text-purple-600' },
    { key: 'countries', label: 'Countries', format: 'number', color: 'text-orange-600' },
  ],
  cta_url_tracking: [
    { key: 'customer_name', label: 'Customer', sticky: true, searchable: true },
    { key: 'sender_id', label: 'Sender ID', searchable: true },
    { key: 'campaign_id', label: 'Campaign', searchable: true },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'url_messages', label: 'With URLs', format: 'number', color: 'text-blue-600', sortable: true },
    { key: 'total_urls', label: 'Total URLs', format: 'number', color: 'text-indigo-600' },
    { key: 'url_pct', label: 'URL %', format: 'percentage', color: 'text-blue-600', sortable: true },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600' },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge', sortable: true },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'countries', label: 'Countries', format: 'number', color: 'text-orange-600' },
  ],
  campaign_type_segmentation: [
    { key: 'campaign_type', label: 'Type', sticky: true },
    { key: 'channel', label: 'Channel' },
    { key: 'total_messages', label: 'Messages', format: 'number', color: 'text-gray-900', sortable: true },
    { key: 'delivered', label: 'Delivered', format: 'number', color: 'text-green-600' },
    { key: 'failed', label: 'Failed', format: 'number', color: 'text-red-600' },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'rate_badge', sortable: true },
    { key: 'revenue', label: 'Revenue', format: 'currency', color: 'text-gray-900', sortable: true },
    { key: 'cost', label: 'Cost', format: 'currency', color: 'text-gray-900' },
    { key: 'profit', label: 'Profit', format: 'profit', sortable: true },
    { key: 'customers', label: 'Customers', format: 'number', color: 'text-blue-600' },
    { key: 'sender_ids', label: 'Sender IDs', format: 'number', color: 'text-purple-600' },
    { key: 'avg_delivery_time', label: 'Avg Delivery', format: 'duration', color: 'text-gray-900' },
  ],
};

// ─── Cell renderer ────────────────────────────────────────────
const renderCell = (value, col) => {
  if (col.render) return col.render(value);
  if (value == null || value === '') return <span className="text-gray-300">—</span>;

  switch (col.format) {
    case 'number':
      return <span className={col.color || 'text-gray-900'}>{formatNumber(value)}</span>;
    case 'currency':
      return <span className={col.color || 'text-gray-900'}>{formatCurrency(value)}</span>;
    case 'currency_precise':
      return <span className={col.color || 'text-gray-600'}>{(value || 0).toFixed(4)}</span>;
    case 'percentage':
      return <span className={col.color || 'text-gray-900'}>{formatPercentage(value)}</span>;
    case 'duration':
      return <span className={col.color || 'text-gray-900'}>{formatDuration(value)}</span>;
    case 'decimal':
      return <span className={col.color || 'text-gray-900'}>{(value || 0).toFixed(2)}</span>;
    case 'profit':
      return (
        <span className={(value || 0) >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
          {formatCurrency(value)}
        </span>
      );
    case 'margin_pct':
      return (
        <span className={(value || 0) >= 10 ? 'text-green-600' : (value || 0) >= 0 ? 'text-yellow-600' : 'text-red-600'}>
          {formatPercentage(value)}
        </span>
      );
    case 'error_pct':
      return (
        <span className={(value || 0) > 10 ? 'text-red-600 font-medium' : (value || 0) > 5 ? 'text-orange-600' : 'text-gray-900'}>
          {formatPercentage(value)}
        </span>
      );
    case 'rate_badge':
      return (
        <div className="flex items-center">
          <div className="w-12 bg-gray-200 rounded-full h-2 mr-2">
            <div className="bg-green-600 h-2 rounded-full" style={{ width: `${Math.min(value || 0, 100)}%` }} />
          </div>
          <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(value || 0)}`}>
            {formatPercentage(value)}
          </span>
        </div>
      );
    case 'array':
      return <span className="text-xs text-gray-600">{Array.isArray(value) ? value.join(', ') : String(value)}</span>;
    default:
      return <span className={col.color || 'text-gray-900'}>{String(value)}</span>;
  }
};


// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════

const MISReports = () => {
  const { isCustomer } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('etl_date_range');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          time_range: parsed.preset || parsed.time_range || 'last_7_days',
          start_date: parsed.start_date || '',
          end_date: parsed.end_date || ''
        };
      } catch (e) { /* fall through */ }
    }
    return { time_range: 'last_7_days', start_date: '', end_date: '' };
  });
  const [sortBy, setSortBy] = useState('total_messages');
  const [sortOrder, setSortOrder] = useState('desc');
  const [limit, setLimit] = useState(100);

  // Segmentation filters
  const [segChannel, setSegChannel] = useState('');
  const [segCampaignType, setSegCampaignType] = useState('');

  const [columnFilters, setColumnFilters] = useState({});
  const filterRefs = useRef({});

  // ─── Load catalog ──────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall('/api/reports/catalog');
        const data = await res.json();
        setCatalog(data);
      } catch (err) {
        console.error('Failed to load report catalog:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ─── Execute report ────────────────────────────────
  const executeReport = useCallback(async (report, overrideSortBy, overrideSortOrder) => {
    setActiveReport(report);
    setReportError(null);
    setReportLoading(true);
    const effectiveSort = overrideSortBy || sortBy;
    const effectiveOrder = overrideSortOrder || sortOrder;
    try {
      const params = new URLSearchParams();
      if (filters.time_range === 'custom') {
        if (filters.start_date) params.set('start_date', filters.start_date);
        if (filters.end_date) params.set('end_date', filters.end_date);
      } else {
        params.set('time_range', filters.time_range);
      }
      params.set('sort_by', effectiveSort);
      params.set('sort_order', effectiveOrder);
      params.set('limit', String(limit));
      if (segChannel) params.set('channels', segChannel);
      if (segCampaignType) params.set('campaign_type', segCampaignType);

      const res = await apiCall(`/api/reports/execute/${report.id}?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setReportData(json);
    } catch (err) {
      console.error('Report execution failed:', err);
      setReportError(err.message);
    } finally {
      setReportLoading(false);
    }
  }, [filters, sortBy, sortOrder, limit, segChannel, segCampaignType]);

  // ─── Sort handler (re-fetches from server) ─────────
  const handleReportSort = (column) => {
    let newOrder = 'desc';
    if (sortBy === column) {
      newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    }
    setSortBy(column);
    setSortOrder(newOrder);
    if (activeReport) {
      executeReport(activeReport, column, newOrder);
    }
  };

  // ─── Column filter handling ────────────────────────
  const handleFilterApply = () => {
    const newFilters = {};
    Object.keys(filterRefs.current).forEach(key => {
      if (filterRefs.current[key]) {
        newFilters[key] = filterRefs.current[key].value || '';
      }
    });
    setColumnFilters(newFilters);
  };

  const clearAllColumnFilters = () => {
    Object.keys(filterRefs.current).forEach(key => {
      if (filterRefs.current[key]) filterRefs.current[key].value = '';
    });
    setColumnFilters({});
  };

  const getFilteredRows = (rows) => {
    if (!rows || !Array.isArray(rows)) return rows;
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v && v.trim());
    if (activeFilters.length === 0) return rows;
    return rows.filter(row =>
      activeFilters.every(([key, val]) => {
        const cellVal = row[key];
        if (cellVal == null) return false;
        return String(cellVal).toLowerCase().includes(val.toLowerCase());
      })
    );
  };

  // ─── Filter catalog ────────────────────────────────
  const filtered = catalog.filter(r => {
    if (activeCategory !== 'all' && r.category !== activeCategory) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return r.name.toLowerCase().includes(q) ||
             r.description.toLowerCase().includes(q) ||
             (r.tags || []).some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  // ─── CSV Export ────────────────────────────────────
  const exportCSV = () => {
    if (!reportData?.data) return;
    const data = Array.isArray(reportData.data) ? reportData.data :
                 reportData.data.summary ? [reportData.data.summary] : [];
    if (data.length === 0) return;
    const keys = Object.keys(data[0]);
    const csv = [keys.join(','), ...data.map(r => keys.map(k => {
      const v = r[k];
      return Array.isArray(v) ? `"${v.join(', ')}"` : JSON.stringify(v ?? '');
    }).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeReport?.id || 'report'}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ═══════════════════════════════════════════════════
  // SortableHeader — matches DetailPopup exactly
  // ═══════════════════════════════════════════════════
  const SortableHeader = ({ column, label, sortable = true }) => {
    const isActive = sortBy === column;
    return (
      <th className={`px-3 py-2 text-left sticky top-0 ${isActive ? 'bg-blue-50' : 'bg-gray-50'}`}>
        {sortable ? (
          <button
            onClick={() => handleReportSort(column)}
            className={`flex items-center space-x-1 text-xs font-semibold uppercase tracking-wider hover:text-blue-600 transition-colors ${
              isActive ? 'text-blue-700' : 'text-gray-700'
            }`}
          >
            <span>{label}</span>
            {isActive ? (
              sortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3 opacity-30" />
            )}
          </button>
        ) : (
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">{label}</div>
        )}
      </th>
    );
  };

  // ═══════════════════════════════════════════════════
  // SearchableHeader — matches DetailPopup exactly
  // ═══════════════════════════════════════════════════
  const SearchableHeader = ({ column, label, filterKey, sortable = true }) => {
    const isActive = sortBy === column;
    return (
      <th className={`px-3 py-2 text-left sticky top-0 ${isActive ? 'bg-blue-50' : 'bg-gray-50'}`}>
        <div className="space-y-2">
          {sortable ? (
            <button
              onClick={() => handleReportSort(column)}
              className={`flex items-center space-x-1 text-xs font-semibold uppercase tracking-wider hover:text-blue-600 transition-colors ${
                isActive ? 'text-blue-700' : 'text-gray-700'
              }`}
            >
              <span>{label}</span>
              {isActive ? (
                sortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3 opacity-30" />
              )}
            </button>
          ) : (
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">{label}</div>
          )}
          <div className="relative">
            <input
              ref={el => { filterRefs.current[filterKey] = el; }}
              type="text"
              defaultValue={columnFilters[filterKey] || ''}
              onBlur={handleFilterApply}
              onKeyPress={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              placeholder="Filter..."
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={() => {
                if (filterRefs.current[filterKey]) {
                  filterRefs.current[filterKey].value = '';
                  handleFilterApply();
                }
              }}
              className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      </th>
    );
  };

  // ═══════════════════════════════════════════════════
  // Report detail view
  // ═══════════════════════════════════════════════════
  if (activeReport) {
    const rawColumns = REPORT_COLUMNS[activeReport.id];
    // For customer-scoped users, drop any column whose key is sensitive.
    // The backend already strips the values, so without this the cells render
    // as empty/zero placeholders — filtering at render-time keeps the table clean.
    const columns = isCustomer
      ? rawColumns?.filter(col => !CUSTOMER_HIDDEN_KEYS.has(col.key))
      : rawColumns;
    const rawRows = reportData?.data;
    const isSummaryReport = activeReport.id === 'traffic_summary' && rawRows?.summary;
    const tableRows = isSummaryReport ? null : getFilteredRows(rawRows);
    const hasActiveFilters = Object.values(columnFilters).some(v => v && v.trim());

    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Row 1 — Title + Actions */}
          <div className="px-6 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex justify-between items-center">
            <div className="flex items-center space-x-3 min-w-0">
              <button onClick={() => { setActiveReport(null); setReportData(null); setReportError(null); setColumnFilters({}); filterRefs.current = {}; }}
                className="p-1.5 rounded-lg hover:bg-white/60 transition-colors flex-shrink-0">
                <ArrowLeft size={18} className="text-gray-600" />
              </button>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 flex items-center space-x-2 truncate">
                  {React.createElement(resolveIcon(activeReport.icon), { size: 18, className: 'text-blue-600 flex-shrink-0' })}
                  <span className="truncate">{activeReport.name}</span>
                  {tableRows && (
                    <span className="text-xs font-normal text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full flex-shrink-0">
                      {tableRows.length} rows
                    </span>
                  )}
                </h2>
                <p className="text-xs text-gray-500 truncate">{activeReport.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              {hasActiveFilters && (
                <button onClick={clearAllColumnFilters}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  Clear Filters
                </button>
              )}
              <button onClick={() => executeReport(activeReport)}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1.5 text-sm font-medium">
                <RefreshCw size={13} className={reportLoading ? 'animate-spin' : ''} />
                <span>{reportLoading ? 'Running…' : 'Run'}</span>
              </button>
              {reportData && (
                <button onClick={exportCSV}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-1 text-xs text-gray-600">
                  <Download size={13} /><span>CSV</span>
                </button>
              )}
            </div>
          </div>

          {/* Row 2 — Filters bar with labeled groups */}
          <div className="px-6 py-2.5 border-b bg-gray-50 flex items-center gap-4 text-xs overflow-x-auto">
            {/* Date range */}
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400 font-medium uppercase tracking-wider">Period</span>
              <select value={filters.time_range} onChange={(e) => setFilters(f => ({ ...f, time_range: e.target.value }))}
                className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500">
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="last_90_days">Last 90 Days</option>
                <option value="custom">Custom</option>
              </select>
              {filters.time_range === 'custom' && (
                <>
                  <input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))}
                    className="border border-gray-300 rounded px-1.5 py-1 text-xs" />
                  <span className="text-gray-400">→</span>
                  <input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))}
                    className="border border-gray-300 rounded px-1.5 py-1 text-xs" />
                </>
              )}
            </div>

            <div className="w-px h-5 bg-gray-300" />

            {/* Channel */}
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400 font-medium uppercase tracking-wider">Channel</span>
              <select value={segChannel} onChange={e => setSegChannel(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs bg-white">
                <option value="">All</option>
                <option value="SMPP">SMPP</option>
                <option value="HTTP">HTTP</option>
                <option value="WA">WhatsApp</option>
                <option value="SIP">SIP</option>
                <option value="APP">APP</option>
                <option value="ENUM">ENUM</option>
                <option value="SIG_M3UA">SIG_M3UA</option>
              </select>
            </div>

            <div className="w-px h-5 bg-gray-300" />

            {/* Campaign type */}
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400 font-medium uppercase tracking-wider">Type</span>
              <select value={segCampaignType} onChange={e => setSegCampaignType(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs bg-white">
                <option value="">All</option>
                <option value="promo">Promotional</option>
                <option value="trans">Transactional</option>
                <option value="2-way">2-Way</option>
              </select>
            </div>

            <div className="w-px h-5 bg-gray-300" />

            {/* Rows */}
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400 font-medium uppercase tracking-wider">Rows</span>
              <select value={limit} onChange={e => setLimit(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-xs bg-white">
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </div>

            {/* Active filter indicators */}
            {(segChannel || segCampaignType) && (
              <>
                <div className="w-px h-5 bg-gray-300" />
                <div className="flex items-center gap-1">
                  {segChannel && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                      {segChannel}
                      <button onClick={() => setSegChannel('')} className="hover:text-blue-900"><X size={10} /></button>
                    </span>
                  )}
                  {segCampaignType && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                      {segCampaignType}
                      <button onClick={() => setSegCampaignType('')} className="hover:text-purple-900"><X size={10} /></button>
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Traffic Summary — special KPI layout */}
          {isSummaryReport && !reportLoading && (
            <TrafficSummaryView data={rawRows} />
          )}

          {/* Table body */}
          {!isSummaryReport && (
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
              {reportLoading && (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Executing report…</span>
                </div>
              )}

              {reportError && !reportLoading && (
                <div className="m-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                  <strong>Error:</strong> {reportError}
                </div>
              )}

              {!reportLoading && !reportError && tableRows && tableRows.length > 0 && columns && (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      {columns.map(col =>
                        col.searchable ? (
                          <SearchableHeader key={col.key} column={col.sortable ? col.key : ''} label={col.label}
                            filterKey={col.key} sortable={!!col.sortable} />
                        ) : (
                          <SortableHeader key={col.key} column={col.key} label={col.label} sortable={!!col.sortable} />
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tableRows.map((row, idx) => (
                      <tr key={idx} className="hover:bg-blue-50 transition-colors">
                        {columns.map(col => (
                          <td key={col.key}
                            className={`px-3 py-3 text-sm whitespace-nowrap ${
                              col.sticky ? 'sticky left-0 bg-white border-r z-10 font-medium' : ''
                            } ${col.format && col.format !== 'rate_badge' && col.format !== 'array' ? 'text-right' : ''}`}
                          >
                            {renderCell(row[col.key], col)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Fallback for reports without a column schema — auto-generate */}
              {!reportLoading && !reportError && tableRows && tableRows.length > 0 && !columns && (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      {Object.keys(tableRows[0]).map(key => (
                        <th key={key} className="px-3 py-2 text-left sticky top-0 bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-700">
                          {key.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tableRows.map((row, idx) => (
                      <tr key={idx} className="hover:bg-blue-50 transition-colors">
                        {Object.entries(row).map(([key, val]) => (
                          <td key={key} className={`px-3 py-3 text-sm whitespace-nowrap ${typeof val === 'number' ? 'text-right' : ''}`}>
                            {val == null ? '—' : Array.isArray(val) ? val.join(', ') : typeof val === 'number' ? formatNumber(val) : String(val)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {!reportLoading && !reportError && tableRows && tableRows.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <div className="text-6xl mb-4">🔍</div>
                  <p className="text-lg font-medium">No records found</p>
                  <p className="text-sm">Try adjusting your filters or date range</p>
                  {hasActiveFilters && (
                    <button onClick={clearAllColumnFilters}
                      className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                      Clear All Filters
                    </button>
                  )}
                </div>
              )}

              {!reportLoading && !reportError && !tableRows && !isSummaryReport && (
                <div className="text-center py-20 text-gray-400">
                  <BarChart2 size={48} className="mx-auto mb-3 opacity-40" />
                  <p className="text-lg">Click <strong>Run Report</strong> to execute</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // Catalog view
  // ═══════════════════════════════════════════════════
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex items-center justify-between">
          <div className="flex space-x-1 overflow-x-auto">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.key;
              const count = cat.key === 'all' ? catalog.length : catalog.filter(r => r.category === cat.key).length;
              return (
                <button key={cat.key} onClick={() => setActiveCategory(cat.key)}
                  className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}>
                  <Icon size={14} />
                  <span>{cat.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative ml-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search reports…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading reports…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Search size={48} className="mx-auto mb-3 opacity-40" />
          <p>No reports match your search</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(report => {
            const Icon = resolveIcon(report.icon);
            return (
              <button key={report.id} onClick={() => executeReport(report)}
                className="bg-white rounded-xl border border-gray-200 p-5 text-left hover:shadow-md hover:border-blue-300 transition-all group">
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors">
                    <Icon size={20} className="text-blue-600" />
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500 capitalize">{report.category}</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm">{report.name}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{report.description}</p>
                {report.tags && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {report.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-xs px-2 py-0.5 bg-gray-50 text-gray-400 rounded">{tag}</span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ─── Traffic Summary special view ─────────────────────────────
const TrafficSummaryView = ({ data }) => {
  const { isCustomer } = useAuth();
  const s = data.summary;
  const allKpis = [
    { label: 'Total Messages', value: formatNumber(s.total_messages), color: 'text-blue-600' },
    { label: 'Delivered', value: formatNumber(s.delivered), color: 'text-green-600' },
    { label: 'Failed', value: formatNumber(s.failed), color: 'text-red-600' },
    { label: 'Delivery Rate', value: formatPercentage(s.delivery_rate), color: s.delivery_rate >= 90 ? 'text-green-600' : 'text-orange-600' },
    { label: 'Revenue', value: formatCurrency(s.total_revenue), color: 'text-gray-900' },
    { label: 'Cost', value: formatCurrency(s.total_cost), color: 'text-gray-900', sensitive: true },
    { label: 'Profit', value: formatCurrency(s.total_profit), color: s.total_profit >= 0 ? 'text-green-600' : 'text-red-600', sensitive: true },
    { label: 'Customers', value: formatNumber(s.unique_customers), color: 'text-purple-600' },
  ];
  const kpis = isCustomer ? allKpis.filter(k => !k.sensitive) : allKpis;

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {kpis.map(k => (
          <div key={k.label} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-gray-500 mb-1">{k.label}</div>
            <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {data.channels?.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-900 mb-3 text-sm">Channel Breakdown</h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-700">Channel</th>
                <th className="px-3 py-2 text-right bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-700">Count</th>
                <th className="px-3 py-2 text-left bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-700">Delivery Rate</th>
                <th className="px-3 py-2 text-right bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-700">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.channels.map((ch, i) => (
                <tr key={i} className="hover:bg-blue-50 transition-colors">
                  <td className="px-3 py-3 text-sm"><span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">{ch.channel}</span></td>
                  <td className="px-3 py-3 text-sm text-gray-900 text-right">{formatNumber(ch.count)}</td>
                  <td className="px-3 py-3 text-sm">
                    <div className="flex items-center">
                      <div className="w-12 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-green-600 h-2 rounded-full" style={{ width: `${Math.min(ch.delivery_rate || 0, 100)}%` }} />
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(ch.delivery_rate || 0)}`}>
                        {formatPercentage(ch.delivery_rate)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900 text-right">{formatCurrency(ch.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.statuses?.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-900 mb-3 text-sm">Status Distribution</h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-700">Status</th>
                <th className="px-3 py-2 text-right bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-700">Count</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.statuses.map((st, i) => (
                <tr key={i} className="hover:bg-blue-50 transition-colors">
                  <td className="px-3 py-3 text-sm">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      st.status === 'DELIVERED' ? 'bg-green-100 text-green-800' :
                      st.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>{st.status}</span>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900 text-right font-medium">{formatNumber(st.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Top-level wrapper with MIS / Intelligence tabs
// ═══════════════════════════════════════════════════════════════
function ReportsLibrary() {
  const [activeTab, setActiveTab] = useState(() => {
    // Persist tab selection across refreshes
    return localStorage.getItem('reports_library_tab') || 'mis';
  });

  const switchTab = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('reports_library_tab', tab);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Page header — always visible, above tabs */}
      <div className="bg-white border-b border-gray-200 px-6 pt-5 pb-3">
        <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center space-x-2">
          <FileText size={22} className="text-blue-600" />
          <span>Reports Library</span>
        </h1>
        <p className="text-sm text-gray-600">
          Pre-built analytics reports and intelligence insights for your messaging business
        </p>
      </div>

      {/* Tab bar */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex items-center space-x-1">
          <button
            onClick={() => switchTab('mis')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center space-x-2 ${
              activeTab === 'mis'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText size={16} />
            <span>MIS Reports</span>
          </button>
          <button
            onClick={() => switchTab('intelligence')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center space-x-2 ${
              activeTab === 'intelligence'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Brain size={16} />
            <span>Intelligence</span>
            
          </button>
        </div>
      </div>

      {/* Body */}
      {activeTab === 'mis' ? <MISReports /> : <IntelligenceLibrary />}
    </div>
  );
}

export default ReportsLibrary;

